// 決定的な擬似乱数（PRNG）。放置ゲームの中核 `simulate` は決定的純関数なので、
// 「同じ state + elapsedSec なら必ず同じ結果」を守るために乱数も状態として持つ。
//
// mulberry32: 32bit seed の軽量・高品質 PRNG。seed は GameState に焼かれ、来店
// 抽選を進めるたびに「次の seed」を返す。実時刻・Math.random・グローバル状態には
// 一切触れない（テストで決定性を検証する）。

/** 1 ステップぶんの乱数結果。`value` は [0,1)、`next` は次に渡す seed。 */
export interface RngStep {
  /** [0, 1) の乱数。 */
  value: number;
  /** 次の抽選に渡す seed（32bit 符号なし）。 */
  next: number;
}

/**
 * mulberry32。与えた seed から [0,1) の乱数 1 つと次の seed を返す純関数。
 *
 * 状態を引数で受け渡す形にしているので、呼び出し側（simulate）は seed を
 * GameState に保持して進められる。クロージャを作らないのは決定性を
 * 引数だけで閉じるため。
 *
 * @param seed 32bit に丸めて使う（負値・小数も `>>> 0` で正規化）。
 */
export function nextRandom(seed: number): RngStep {
  let a = seed >>> 0;
  a = (a + 0x6d2b79f5) >>> 0;
  let t = a;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const value = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return { value, next: a };
}

/**
 * seed から [min, max) の整数を 1 つ返す（next seed も返す）。
 * 来店判定の「分」単位ループなどで使う補助。
 */
export function nextIntRange(seed: number, min: number, max: number): { value: number; next: number } {
  const r = nextRandom(seed);
  const span = Math.max(0, max - min);
  return { value: min + Math.floor(r.value * span), next: r.next };
}

/**
 * 文字列を 32bit seed に畳み込む（FNV-1a 風）。テストやデバッグで
 * 「人間が読める seed 名 → 数値 seed」を作るのに使える。GameState は数値 seed を
 * 持つので本筋では使わないが、将来の固定シナリオ用に置いておく。
 */
export function hashSeed(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
